import { Button } from "./../../components/Button/Button";
import { DataContext } from "./../../context/DataContext";
import { Footer } from "./../../components/Footer/Footer";
import { Fullpage } from "../../components/Fullpage/Fullpage";
import { Howl } from "howler";
import { Link } from "react-router-dom";
import { ReactComponent as ArrowIcon } from "./../../assets/arrow.svg";
import { ReactComponent as ArrowIconUp } from "./../../assets/arrow-up.svg";
import { ReactComponent as Pause } from "./../../assets/pause.svg";
import { ReactComponent as Play } from "./../../assets/play.svg";
import { useWindowSize } from "usehooks-ts";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import posterPlaceholder from "./../../assets/poster-placeholder.jpg";
import React from "react";
import sfx from "./../../assets/sound_01.mp3";

import "./section.scss";

const PlaygroundLazy = React.lazy(() =>
	import("../../components/Playground/Playground").then((m) => ({ default: m.Playground }))
);

const Section = ({ className, children, options, onArrowClick, playClick, ...props }) => {
	const theme = options?.theme === `dark` ? `is-dark` : `is-light`;
	const { height } = useWindowSize();

	return (
		<div
			{...props}
			style={{ height }}
			className={[`section`, `section--${theme}`, className].join(` `)}>
			{children}
			{onArrowClick && (
				<div className={`section-arrow`} onClick={() => { onArrowClick(); playClick?.(); }}>
					<ArrowIcon className={`section-arrow-icon`} />
				</div>
			)}
		</div>
	);
};

const Quote = ({ size, title, text, button, options }) => {
	return (
		<React.Fragment>
			<div className={[`section-content`].join(` `)}>
				<div className={`section-content-quote`}>
					{title && (
						<h3
							className={[`_${size ? size : `big`}`, `centered`].join(` `)}
							dangerouslySetInnerHTML={{ __html: title }}
						/>
					)}
					{text && (
						<p
							className={[`_base`, `centered`].join(` `)}
							dangerouslySetInnerHTML={{ __html: text }}
						/>
					)}
					{button.label && <Button {...button} />}
				</div>
			</div>
		</React.Fragment>
	);
};

const Video = ({ file, poster, caption, controls_color }) => {
	const [playing, setPlaying] = React.useState(false);
	const videoRef = React.useRef();
	const [hovering, setHovering] = React.useState(false);
	const hoverTimer = React.useRef();

	const togglePlay = () => {
		const play = () => {
			videoRef.current.play();
		};

		const pause = () => {
			videoRef.current.pause();
		};

		!playing ? play() : pause();

		setPlaying(!playing);
	};

	const handlePause = () => {
		setPlaying(false);
	};

	const toggleHovering = () => {
		setHovering(true);
		clearInterval(hoverTimer.current);
		hoverTimer.current = setTimeout(() => setHovering(false), 500);
	};

	React.useEffect(() => {
		return clearInterval(hoverTimer.current);
	}, []);

	return (
		<React.Fragment>
			<div className={`section-content`}>
				<video
					onPause={handlePause}
					ref={videoRef}
					className={`section-content-video`}
					preload={true.toString()}
					poster={poster ? poster.sizes.large : posterPlaceholder}
					playsInline
					nocontrols={true.toString()}>
					<source src={file.url} type='video/mp4' />
					Your browser does not support the video tag.
				</video>
				<div
					onMouseMove={toggleHovering}
					className={[
						`section-content-controls`,
						`section-content-controls--${controls_color === `light` ? `is-light` : `is-dark`}`,
						`section-content-controls--${playing ? `is-playing` : `not-playing`}`,
						`section-content-controls--${hovering ? `is-hovering` : `not-hovering`}`,
					].join(` `)}
					onClick={() => togglePlay()}>
					{playing ? <Pause /> : <Play />}
				</div>
			</div>
			<div className={`section-caption`}>
				<p
					className={[`_base`, `centered`].join(` `)}
					dangerouslySetInnerHTML={{ __html: caption }}
				/>
			</div>
		</React.Fragment>
	);
};

export const Home = React.forwardRef((props, fullPageRef) => {
	const { t } = useTranslation();
	const pages = React.useContext(DataContext).pages;
	const page = pages && pages.find((page) => page.slug === `home`);
	const contents = page?.acf?.content ?? [];

	const trackRef = React.useRef(null);
	React.useEffect(() => {
		trackRef.current = new Howl({ src: sfx, html5: true, preload: true });
		return () => { trackRef.current?.stop(); };
	}, []);
	const playClick = React.useCallback(() => trackRef.current?.play(), []);

	const scrollTo = (id) => {
		const fullpage = document.getElementById("fullpage");
		const target = document.getElementById(id);
		fullpage.scrollTo({ top: target.offsetTop });
	};

	return (
		<React.Fragment>
			<style
				dangerouslySetInnerHTML={{
					__html: `
						html, body, #root {
							overflow: hidden;
						}

						.header {
							position: fixed;
							width: 100%;
						}
					`,
				}}
			/>
			{contents.length > 0 && (
				<Fullpage>
					<Section playClick={playClick} onArrowClick={() => scrollTo("section-1")} id='section-0'>
						<React.Suspense fallback={null}>
							<PlaygroundLazy quote={page.acf.playground_text} />
						</React.Suspense>
					</Section>
					{contents.map((content, index) => {
						switch (content.acf_fc_layout) {
							case `quote`:
								return (
									<Section
										id={`section-${index + 1}`}
										key={content.acf_fc_layout + `-` + index}
										playClick={playClick}
										onArrowClick={() => scrollTo(`section-${index + 2}`)}
										{...content}>
										<Quote {...content} />
									</Section>
								);
							case `video`:
								return (
									<Section
										id={`section-${index + 1}`}
										key={content.acf_fc_layout + `-` + index}
										playClick={playClick}
										onArrowClick={() => scrollTo(`section-${index + 2}`)}
										{...content}>
										<Video {...content} />
									</Section>
								);
							default:
								return false;
						}
					})}
					<Section id={`section-${contents.length + 1}`} className={`fp-auto-height`}>
						<Footer />
						<div className={`section-back-to-top`} onClick={() => scrollTo(`section-0`)}>
							<ArrowIconUp className={`section-arrow-icon section-arrow-icon--white`} />
							<span className={`_base`}>{t("home_back")}</span>
						</div>
					</Section>
				</Fullpage>
			)}
		</React.Fragment>
	);
});
